<template>
  <v-main>
    <MenuBar :layers="layers" :incidentProfiles="profiles" :user="user"
                        :countedMmsiAnomalies="countedMmsiAnomalies"
                        :startDate="date" :endDate="endDate"
                        :consecutive="consecutive"
                        :latest-anomalies="events"
                        :latest-incidents="incidentEvents"
                        @anomalyFilter="anomalyFilter"
                        @incidentProfileFilter="incidentProfileFilter"
                        @neighbourValues="updateValues" @layerEvent="layerEvent"
                        @selectedMmsi="setMmsi" @showAnomaly="showAnomaly" @showIncident="showIncident">
    </MenuBar>

  <div id="mapContainer">
    <CoordinateComponent :coordinate="mouseCoordinate" :zoom="zoomLevel"></CoordinateComponent>
    <StatusBar :dateStart="date" :dateEnd="endDate" ></StatusBar>
    <DistinctMmsiBar :startDate="date"></DistinctMmsiBar>
    <CurrentMapStats :stats="statsInView"></CurrentMapStats>
  </div>

  <div id="lineContainer">
    <DataSelection :chartData="chartData" :loaded="loaded" @range="rangeChart" @date="dateChart"
                   @clicked="dateSelect">
    </DataSelection>
  </div>

  </v-main>
</template>
<script>

import "maplibre-gl/dist/maplibre-gl.css";
import maplibregl from 'maplibre-gl';
import moment from 'moment'
import warningImage from '../assets/warning.png';

import MenuBar from '@/components/MenuBar.vue';
import AnomalyReasonText from "@/utils/AnomalyReasonText";
import { Auth } from "aws-amplify";
import StatusBar from "@/components/StatusBar";
import MapService from "@/services/MapService";
import DataSelection from "@/components/DataSelection";

import api from "@/services/Api";
import authorizedFetch from "@/services/AuthorizedFetch";
import DistinctMmsiBar from "@/components/DistinctMmsiBar";
import CoordinateComponent from "@/components/CoordinateComponent.vue";
import CurrentMapStats from "@/components/CurrentMapStats.vue";
import geojsonRbush from 'geojson-rbush'
import { EventBus } from "@/utils/eventBus";

const ANOMALY_LAYER_ID = 'anomaly-vector-l'
const INCIDENT_LAYER_ID = 'incident-vector-l'
const INCIDENT_LAYER_ID_SYMBOL = 'incident-vector-l-symbol'
const INCIDENT_COLOR = '#f65d4c'
const ANOMALY_COLOR = '#E19C35'

export default {
  name: "MapView",
  components: {CoordinateComponent, DistinctMmsiBar, DataSelection, StatusBar, MenuBar, CurrentMapStats },
  data() {
    return {
      center: [12.2, 65.463],
      map: undefined,
      layerControl: {},
      chartData: {},
      anomalyLayer: {},
      layerGroup: {},
      date: undefined,
      endDate: undefined,
      layers: [],
      minutes: 300,
      radius: 1000,
      consecutive: 3,
      vessels: 3,
      zoomLevel: 4,
      mouseCoordinate: '',
      profiles: [],
      hours: 24,
      user: 'test',
      highlightMmsiLayer: undefined,
      mmsi: 0,
      mmsiAnomalies: {
        'type': 'FeatureCollection',
        'features': []
      },
      countedMmsiAnomalies: undefined,
      loaded: false,
      exclusionAreas: {
        'type': 'FeatureCollection',
        'features': []
      },
      events: [],
      incidentEvents: [],
      anomalyIndex: geojsonRbush(),
      incidentIndex: geojsonRbush(),
      statsInView: {
        anomalies: 'N/A',
        incidents: 'N/A',
        vessels: 'N/A'
      },
      anomalySound: null,
      incidentSound: null,
      soundInitialized: false,
      soundEnabled: false,
      anomalyTileVersion: 0,
      incidentTileVersion: 0
    }
  },
  methods: {
    setupMap: function () {
      const cmap = new maplibregl.Map({
        container: 'mapContainer',
        style: 'https://api.maptiler.com/maps/bright-v2/style.json?key=wVAa9X69krko1sqQfQmc',
        center: this.center,
        zoom: this.zoomLevel

      });

      cmap.on('load', async () => {
        cmap.loadImage(warningImage, (error, image) => {
          if (error) {
            console.log('error finding image')
            throw error;
          }
          cmap.addImage('incident-icon', image);
        });
        const fishingZoneSourceId = 'fiskerigrense'
        const fishingZoneSource = MapService.getFishingZoneSource()
        const fishingZoneLayer = MapService.getFishingZoneLayer(fishingZoneSourceId)
        cmap.addSource(fishingZoneSourceId, fishingZoneSource)
        cmap.addLayer(fishingZoneLayer)

        const topoSourceId = 'kartverket-topo'
        const topoSource = MapService.getKartverketTopoSource()
        const topoLayer = MapService.getKartverketTopoLayer(topoSourceId)
        cmap.addSource(topoSourceId, topoSource)
        cmap.addLayer(topoLayer)

        if (this.date === undefined) {
          this.date = moment().startOf('day').format('YYYY-MM-DDTHH:mm:ss');
        }

        if (this.endDate === undefined) {
          this.endDate = moment(this.date).add(1, 'days').format('YYYY-MM-DDTHH:mm:ss')
        }
        this.loadAnomalyAndIncidentGeometries();

        cmap.addSource('anomaly-vector', {
          'type': 'vector',
          'tiles': [
            `${window.location.origin}/vector/anomaly/${this.date}/${this.consecutive}/{z}/{x}/{y}?v=${this.anomalyTileVersion}`
          ]
        })
        cmap.addLayer(
            {
              'id': ANOMALY_LAYER_ID,
              'type': 'circle',
              'source': 'anomaly-vector',
              'source-layer': 'anomaly',
              'paint': {
                'circle-color': ANOMALY_COLOR,
                'circle-stroke-color': '#808080',
                'circle-stroke-width': 1,
                'circle-radius': 5.0,
                'circle-opacity': 0.9
              }
            }
        )

        cmap.addSource('incident-vector', {
          'type': 'vector',
          'tiles': [
            `${window.location.origin}/vector/incident/${this.date}/{z}/{x}/{y}?v=${this.incidentTileVersion}`          ]
        })
        console.log('adding incident vector')
        this.addLayer(INCIDENT_LAYER_ID);
        cmap.on('zoom', async () => {
          this.zoomLevel = Math.round(this.map.getZoom());
        })

        let anomalyLayer = {id: ANOMALY_LAYER_ID, name: 'Anomaly', value: true}
        this.layers.push(anomalyLayer)

        let incidentLayer = {id: INCIDENT_LAYER_ID, name: 'Incident', value: true}
        this.layers.push(incidentLayer)

        let highlightSourceId = MapService.getMmsiSource()
        cmap.addSource(highlightSourceId, {
          'type': 'geojson',
          'data': this.mmsiAnomalies
        })
        this.highlightMmsiLayer = MapService.getHighlightLayerWithoutFilter(highlightSourceId)
        cmap.addLayer(this.highlightMmsiLayer)
        cmap.addSource(MapService.getExclusionSource(), {
          'type': 'geojson',
          'data': this.exclusionAreas
        })
        cmap.addLayer(MapService.getExclusionLayer(MapService.getExclusionSource()))
      });

      const popups = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        anchor: 'bottom'
      });
      cmap.on('mouseenter', ANOMALY_LAYER_ID, function (e) {
        cmap.getCanvas().style.cursor = 'pointer';
        var coordinates = e.features[0].geometry.coordinates.slice();

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        const mmsi = e.features[0].properties?.mmsi;
        const properties = e.features[0].properties;
        fetch("/aisd/vesselInformation/" + mmsi)
            .then((response) => response.json())
            .then((data) => {
              console.log(data);
              let vesselName = data.vesselName;
              if (vesselName === undefined) {
                vesselName = "N/A";
              }
              var info = '<h3>Name: ' + vesselName + '</h3>';
              info += 'MMSI: ' + mmsi + '<br/>';
              info += 'ID: ' + properties.id + '<br/>';
              info += 'Reason: ' + AnomalyReasonText.getAnomalyReasonAsText(properties.anomaly_reason) + '<br/>';
              info += 'Number of consecutive: ' + properties.number_of_consecutive_messages + '</br>';
              info += 'Start: ' + properties.start_time + '</br>';
              info += 'End: ' + properties.end_time + '</br>';
              popups.setLngLat(coordinates).setHTML(info).addTo(cmap);

              cmap.on('mouseleave', ANOMALY_LAYER_ID, function (e) {
                cmap.getCanvas().style.cursor = '';
                const popupEl = document.querySelector('.maplibregl-popup');
                if(!popupEl) {
                  popups.remove();
                  return;
                }

                const { x, y } = e.originalEvent;
                const rect = popupEl.getBoundingClientRect();
                const isInPopup = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

                if(isInPopup){
                  popupEl.addEventListener('mouseleave', () => popups.remove(), {once: true});
                }else{
                  popups.remove();
                }
              });
            })
            .catch(error => console.warn(error)
            );
      });

      cmap.on('mousemove', (e) => {
        const lngLat = e.lngLat;
        const lng = this.toDMM(lngLat.lng);
        const lat = this.toDMM(lngLat.lat);
        this.mouseCoordinate = `${lat}N ${lng}E,`;
      });

      cmap.on('click', INCIDENT_LAYER_ID_SYMBOL, function (e) {
        let coordinates = e.lngLat;
        let incidentId = e.features[0].properties.id
        let properties = e.features[0].properties
        let profileId = e.features[0].properties.profile
        let html = '';
        authorizedFetch("/api/incident/neighbours/" + incidentId)
            .then((response) => response.json())
            .then((data) => {
              var profileId = properties.profile
              var info = '<h1>ID: ' + incidentId + '</h1>'
              info += 'Profile: ' + profileId + '<br/>'
              info += 'Start: ' + properties.start_time + '</br>'
              info += 'End: ' + properties.end_time + '</br>'
              info += 'Vessels involved in incident:  </br>'
              for (let i = 0; i < data.length; i++) {
                console.log(data[i]);
                let vessel = data[i]
                info += 'MMSI: ' + vessel.mmsi + ', Name: ' + vessel.vesselName + '</br>'
              }
              info += '<hr class="solid">'

              new maplibregl.Popup()
                  .setLngLat(coordinates)
                  .setHTML(info + html)
                  .addTo(cmap)

            })
            .then(authorizedFetch('/api/incident/profile/' + profileId)
                .then((response) => response.json())
                .then((data) => {
                  let profile = data
                  html += 'Found using profile: ' + profileId + ' with: </br>'
                  html += 'Name: ' + profile.extendedName + '</br>'
                  html += 'Vessels: ' + profile.vessels + '</br>'
                  html += 'Radius: ' + profile.radius + '</br>'
                  html += 'Time in mins: ' + profile.timeInMinutes + '</br>'
                  html += 'Consecutive errors: ' + profile.numberOfConsecutives + '</br>'
                  html += '<h3><a href="/vIncident/' + incidentId + '">View Full</a></h3>'
                }))
      });
      cmap.on('mouseenter', INCIDENT_LAYER_ID_SYMBOL, function () {
        cmap.getCanvas().style.cursor = 'pointer';
      });
      cmap.on('mouseleave', INCIDENT_LAYER_ID_SYMBOL, function () {
        cmap.getCanvas().style.cursor = '';
      });
      
      this.map = cmap;
      this.map.on('moveend', () => {
        this.setCurrentMapStats();
      });
      this.map.getContainer().addEventListener('mouseout', () => {
        this.mouseCoordinate = '';
      })
    },
    dateSelect(index) {
      let label = this.chartData.labels[index]
      this.date = label
      if (index + 1 >= this.chartData.labels.length) {
        this.endDate = moment(this.date).add(this.hours, 'hours').format('YYYY-MM-DDTHH:mm:ss')
      } else {
        this.endDate = this.chartData.labels[index + 1]
      }
      this.loadAnomalyAndIncidentGeometries()
      this.setTiles()
    },
    rangeChart(range) {
      this.hours = range.value
      this.loadChartValues(this.hours, true)
    },
    dateChart(date) {
      if(!date) return;
      this.date = moment(date[0]).format("YYYY-MM-DDTHH:mm:ss")
      if (date[1] === null || date[1] === undefined) {
        date[1] = moment(date[0]).add(this.hours, 'hours').format("YYYY-MM-DDTHH:mm:ss");
      }
      this.endDate = moment(date[1]).format("YYYY-MM-DDTHH:mm:ss")
      this.loadAnomalyAndIncidentGeometries()
      this.setTiles()
      this.loadChartValuesInTimeRange(this.hours)
    },
    addLayer(id) {
      if (id === ANOMALY_LAYER_ID) {
        this.map.addLayer(
            {
              'id': ANOMALY_LAYER_ID,
              'type': 'circle',
              'source': 'anomaly-vector',
              'source-layer': 'anomaly',
              'paint': {
                'circle-color': ANOMALY_COLOR,
                'circle-stroke-color': '#808080',
                'circle-stroke-width': 1,
                'circle-opacity': 0.9
              }
            }
        )
      } else if (id === INCIDENT_LAYER_ID) {
        this.map.addLayer(
            {
              'id': INCIDENT_LAYER_ID,
              'type': 'fill',
              'source': 'incident-vector',
              'source-layer': 'incident',
              'minzoom': 5,
              'maxzoom': 22,
              'paint': {
                'fill-color': INCIDENT_COLOR,
                'fill-opacity': 0.5
              }
            })
        this.map.addLayer(
            {
              'id': INCIDENT_LAYER_ID_SYMBOL,
              'type': 'symbol',
              'source': 'incident-vector',
              'source-layer': 'incident',
              'layout': {
                'icon-image': 'incident-icon',
                'icon-overlap': 'always'
              }
            }
        )
      }
    },
    removeLayer(id) {
      this.map.removeLayer(id)
      if (id === INCIDENT_LAYER_ID) {
        this.map.removeLayer(INCIDENT_LAYER_ID_SYMBOL)
      }
    },
    async setProfiles() {
      authorizedFetch('/api/incident/profiles')
          .then(response => response.json())
          .then((data) => {
            this.profiles = data
            this.profiles.forEach(profile => {
              profile.checked = true;
            })
          });
    },
    async loadIncidents() {
      authorizedFetch('/api/incidents/features')
          .then(response => response.json())
          .then((data) => {
            let features = [];
            for (let i = 0; i < data.length; i++) {
              let feature = JSON.parse(data[i]);

              let coordinates = feature.geometry.coordinates;
              if (coordinates[0] > 180) {
                feature.geometry = feature.properties.valid_geom;
              }
              features.push(feature);
            }
            this.checkSource("incident");
            this.map.getSource("incident").setData({
              "type": "FeatureCollection",
              "features": features
            });
          });
    },
    async loadChartValues(hours, change) {
      this.loaded = false;
      authorizedFetch('/api/incident/count/' + hours + '/30/' + this.consecutive)
          .then((response) => response.json())
          .then((data) => {
            this.setChartData(data, change)
            this.loaded = true
          });
    },
    async loadChartValuesInTimeRange(hours) {
      this.loaded = false
      authorizedFetch('/api/incident/count/' + hours + '/' + this.consecutive + '/' + this.date + '/' + this.endDate)
          .then((response) => response.json())
          .then((data) => {
            this.setChartData(data, false)
            this.loaded = true
          });
    },
    async loadAnomalyAndIncidentGeometries() {
      try {
        const [anomalyData, incidentData] = await Promise.all([
          api(`/anomaly/position/3/${this.date}/${this.endDate}`),
          api(`/incident/geometry/${this.date}/${this.endDate}`)
        ]);
        this.anomalyIndex.clear()
        this.anomalyIndex.load({
          type: "FeatureCollection",
          features: anomalyData.map(JSON.parse)
        })
        this.incidentIndex.clear()
        this.incidentIndex.load({
          type: "FeatureCollection",
          features: incidentData.map(JSON.parse)
        })
        this.setCurrentMapStats();

      } catch (error) {
        console.error("Error loading anomaly and incident geometries:", error);
      }
    },
    initAudio() {
      if (this.soundInitialized) return

      try {
        this.anomalySound = new Audio(require('@/assets/anomaly-alert.mp3'))
        this.incidentSound = new Audio(require('@/assets/incident-alert.mp3'))
        this.soundInitialized = true
      } catch (e) {
        console.error('Could not load audio files', e)
      }
    },
    setCurrentMapStats(){
      const bbox = this.map.getBounds().toArray().flat()
      const bboxPoly = {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [[
            [bbox[0], bbox[1]],
            [bbox[0], bbox[3]],
            [bbox[2], bbox[3]],
            [bbox[2], bbox[1]],
            [bbox[0], bbox[1]]
          ]]
        }
      }
      const anomalyResults = this.anomalyIndex.search(bboxPoly)
      const incidentResults = this.incidentIndex.search(bboxPoly)
      const uniqueMmsi = new Set(anomalyResults.features.map(a => a.properties.mmsi))
      this.statsInView = {
        anomalies: anomalyResults?.features?.length,
        incidents: incidentResults?.features?.length,
        vessels: uniqueMmsi?.size
      }
    },
    setChartData(data, change) {
      let labelArray = [];
      let incidentValues = [];
      let anomalyValues = [];
      for (let i = 0; i < data.length; i++) {
        let feature = data[i];
        labelArray.push(feature.time);
        incidentValues.push(feature.incidents);
        anomalyValues.push(feature.anomalies);
      }
      this.chartData = {
        labels: labelArray,
        datasets: [
          {
            label: "Anomaly",
            backgroundColor: ANOMALY_COLOR,
            data: anomalyValues
          },
          {
            label: "Incidents",
            backgroundColor: INCIDENT_COLOR,
            data: incidentValues
          }
        ]
      }
      if (change) {
        this.dateSelect(data.length - 1)
      }
    },
    checkSource(id) {
      if (this.map.getSource(id) === undefined) {
        this.map.addSource(id, {
          'type': 'geojson',
          'data': {
            'type': 'FeatureCollection',
            'features': []
          }
        });
      }
    },
    updateValues(value) {
      this.radius = value.radius;
      this.minutes = value.minutes;
      this.vessels = value.vessels;
      this.consecutive = value.consecutive;
      this.setTiles();
      this.loadChartValues(this.hours, false);
    },
    layerEvent(layer) {
      if (layer.value) {
        this.addLayer(layer.id);
      } else {
        this.removeLayer(layer.id);
      }
    },
    anomalyFilter(valueSet) {
      let values = Array.from(valueSet)
      this.map.setFilter(ANOMALY_LAYER_ID,
          [
            "any",
            ["in", ["get", "anomaly_reason"], ["literal", values]]
          ]
      );
    },
    incidentProfileFilter(valueSet) {
      let values = Array.from(valueSet)
      this.map.setFilter(INCIDENT_LAYER_ID,
          [
            "any",
            ["in", ["get", "profile"], ["literal", values]]
          ]
      );
      this.map.setFilter(INCIDENT_LAYER_ID_SYMBOL,
          [
            "any",
            ["in", ["get", "profile"], ["literal", values]]
          ]
      );
    },
    setTiles() {
      const anomalySource = this.map.getSource('anomaly-vector')
      if (anomalySource && anomalySource.setTiles) {
        anomalySource.setTiles([
          `${window.location.origin}/vector/anomaly/${this.date}/${this.endDate}/${this.consecutive}/{z}/{x}/{y}?v=${this.anomalyTileVersion}`
        ])
      }

      const incidentSource = this.map.getSource('incident-vector')
      if (incidentSource && incidentSource.setTiles) {
        incidentSource.setTiles([
          `${window.location.origin}/vector/incident/${this.date}/${this.endDate}/{z}/{x}/{y}?v=${this.incidentTileVersion}`
        ])
      }
    },
    async setMmsi(mmsi) {
      if (mmsi === null || mmsi === undefined) {
        this.mmsiAnomalies = {
          'type': 'FeatureCollection',
          'features': []
        }
        this.updateMmsiSource()
        this.countedMmsiAnomalies = undefined
        return
      }
      try {
        const data = await api('/anomaly/geojson/mmsi/' + mmsi + '/' + this.consecutive + '/' + this.date + '/' + this.endDate);
        let features = []
        for (let i = 0; i < data.length; i++) {
          try {
            let feature = JSON.parse(data[i])

            let coordinates = feature.geometry.coordinates
            if (coordinates[0] > 180) {
              feature.geometry = {
                'type': feature.properties.valid_geom.type,
                'coordinates': feature.properties.valid_geom.coordinates
              }
            }
            features.push(feature)
          } catch (e) {
            console.log(e)
          }
        }
        this.mmsiAnomalies = {
          'type': 'FeatureCollection',
          'features': features
        }
        this.countedMmsiAnomalies = features.length
        this.updateMmsiSource()
        this.fitToBounds(features)

      } catch (e) {
        console.log(e)
      }
    },
    showAnomaly(anomaly) {
      try {
        const {lon, lat} = this.parseWKTToPoint(anomaly.wktPos);
        this.map.flyTo({
          center: [lon, lat],
          zoom: 11,
          speed: 1.2,
          curve: 1.5
        });
      } catch (error) {
        console.error("Error parsing WKT or zooming to point:", error);
      }

  },
  showIncident(incident) {
      try {
        const {lon, lat} = this.parseWKTToPoint(incident.wktPos);
        this.map.flyTo({
          center: [lon, lat],
          zoom: 9,
          speed: 1.2,
          curve: 1.5
        });
      } catch (error) {
        console.error("Error parsing WKT or zooming to point:", error);
      }

    },
  updateMmsiSource() {
    let sourceId = MapService.getMmsiSource()
    let source = this.map.getSource(sourceId)

    if (source === undefined) {
      this.map.addSource(sourceId, {
        'type': 'geojson',
        'data': this.mmsiAnomalies
      })
    } else {
      this.map.getSource(sourceId).setData(this.mmsiAnomalies)
    }
  },
  fitToBounds(features) {
    let bounds = new maplibregl.LngLatBounds();

    features.forEach(function (feature) {
      bounds.extend(feature.geometry.coordinates);
    });

    this.map.fitBounds(bounds);
  },
  getStats() {
    fetch('/aisd/stats/' + this.date)
        .then((response) => response.json())
        .then((data) => {
          this.stats = data
        })
  },
  getExclusionAreas() {
    authorizedFetch('api/incident/exclusion-areas')
        .then((response) => response.json())
        .then((data) => {
          let features = []
          for (let i = 0; i < data.length; i++) {
            try {
              let feature = JSON.parse(data[i])
              features.push(feature)
            } catch (e) {
              console.log(e)
            }
          }
          this.exclusionAreas = {
            'type': 'FeatureCollection',
            'features': features
          }
          this.map.getSource(MapService.getExclusionSource()).setData(this.exclusionAreas)
        })
  },
    toDMM(coordinate) {
      const degrees = Math.floor(coordinate);
      const decimalMinutes = (coordinate - degrees) * 60;
      return `${degrees}° ${decimalMinutes.toFixed(3)}'`;
    },
    parseWKTToPoint(wkt) {
      if (!wkt || typeof wkt !== 'string') {
        throw new Error('Invalid WKT input')
      }

      const coordRegex = /(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)/g
      const coords = []
      let match

      while ((match = coordRegex.exec(wkt)) !== null) {
        const lon = parseFloat(match[1])
        const lat = parseFloat(match[2])
        coords.push([lon, lat])
      }

      if (!coords.length) {
        throw new Error('No coordinates found in WKT')
      }

      // Compute simple bbox center
      let minLon = coords[0][0]
      let maxLon = coords[0][0]
      let minLat = coords[0][1]
      let maxLat = coords[0][1]

      for (let i = 1; i < coords.length; i++) {
        const [lon, lat] = coords[i]
        if (lon < minLon) minLon = lon
        if (lon > maxLon) maxLon = lon
        if (lat < minLat) minLat = lat
        if (lat > maxLat) maxLat = lat
      }

      const centerLon = (minLon + maxLon) / 2
      const centerLat = (minLat + maxLat) / 2

      return { lon: centerLon, lat: centerLat }
    },
    initEventBusLive(reconnectAttempts = 0) {
      console.log('Init live event bus for map...')

      authorizedFetch("api/anomaly/events")
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");

            let partialData = "";
            const processStream = ({ done, value }) => {
              if (done) {
                console.log("Stream closed by the server.");
                this.scheduleReconnectionLive(reconnectAttempts);
                return;
              }
              const chunk = decoder.decode(value);
              partialData += chunk;
              let lines = partialData.split("\n");
              partialData = "";

              lines.forEach((line) => {
                if (line.startsWith("data:")) {
                  line = line.substring(5).trim();
                }

                if (line.trim() === "") {
                  return;
                }

                try {
                  const parsedData = JSON.parse(line);
                  console.log("Parsed event data:", parsedData);
                  this.anomalies = parsedData;
                  this.handleIncomingAnomalies(this.anomalies);
                } catch (error) {
                  console.warn("Incomplete data, waiting for more chunks...");
                  partialData += line + "\n";
                }
              });

              return reader.read().then(processStream).catch((error) => {
                console.error("Stream processing error:", error);
                this.scheduleReconnectionLive(reconnectAttempts);
              });
            };
            reader.read().then(processStream).catch((error) => {
              console.error("Error processing stream:", error);
              this.scheduleReconnectionLive(reconnectAttempts);
            });
          })
          .catch((error) => {
            console.error("Error fetching event stream:", error);
          });
    },
    initIncidentBusLive(reconnectAttempts = 0) {
      console.log('Init incident live event bus for map...')

      authorizedFetch("api/incident/events")
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");

            let partialData = "";
            const processStream = ({ done, value }) => {
              if (done) {
                console.log("Stream closed by the server.");
                this.scheduleReconnectionLive(reconnectAttempts);
                return;
              }
              const chunk = decoder.decode(value);
              partialData += chunk;
              let lines = partialData.split("\n");
              partialData = "";

              lines.forEach((line) => {
                if (line.startsWith("data:")) {
                  line = line.substring(5).trim();
                }

                if (line.trim() === "") {
                  return;
                }

                try {
                  const parsedData = JSON.parse(line);
                  console.log("Parsed event data:", parsedData);
                  this.incidents = parsedData;
                  this.handleIncomingIncidents(this.incidents);
                } catch (error) {
                  console.warn("Incomplete data, waiting for more chunks...");
                  partialData += line + "\n";
                }
              });

              return reader.read().then(processStream).catch((error) => {
                console.error("Stream processing error:", error);
                this.scheduleReconnectionLive(reconnectAttempts);
              });
            };
            reader.read().then(processStream).catch((error) => {
              console.error("Error processing stream:", error);
              this.scheduleReconnectionLive(reconnectAttempts);
            });
          })
          .catch((error) => {
            console.error("Error fetching event stream:", error);
          });
    },
    scheduleReconnectionLive(reconnectAttempts) {
      const maxAttempts = 10
      const delay = Math.min(1000 * 2 ** reconnectAttempts, 30000)

      if (reconnectAttempts >= maxAttempts) {
        console.error('Max reconnection attempts reached (live map).')
        return
      }

      console.log(`Reconnecting live stream in ${delay / 1000}s... (Attempt ${reconnectAttempts + 1})`)
      setTimeout(() => {
        this.initEventBusLive(reconnectAttempts + 1)
      }, delay)
    },
    handleIncomingAnomalies(newList) {
      // simple index for events we have already seen
      const oldIds = new Set(this.events.map(e => e.id))
      let hasNew = false

      newList.forEach(a => {
        if (!oldIds.has(a.id)) {
          hasNew = true
          if (a.lon != null && a.lat != null) {
            this.addRippleMarker(a.lon, a.lat, 'anomaly')
            this.playSound('anomaly')
          } else if (a.wktPos) {
            try {
              const { lon, lat } = this.parseWKTToPoint(a.wktPos)
              this.addRippleMarker(lon, lat, 'anomaly')
              this.playSound('anomaly')
            } catch (e) {
              console.warn('not able to parse anomaly: ', e)
            }
          }
        }
      })

      if (hasNew) {
        this.anomalyTileVersion++
        this.setTiles()
      }

      this.events = newList
    },

    handleIncomingIncidents(newList) {
      const oldIds = new Set(this.incidentEvents.map(e => e.id))
      let hasNew = false

      newList.forEach(a => {
        if (!oldIds.has(a.id)) {
          hasNew = true
          if (a.wktPos) {
            try {
              const { lon, lat } = this.parseWKTToPoint(a.wktPos)
              this.addRippleMarker(lon, lat, 'incident')
              this.playSound('incident')
            } catch (e) {
              console.warn('not able to parse anomaly: ', e)
            }
          } else {
             console.log('did not find any wktpos... ' + JSON.stringify(a));
          }
        }
      })
      if (hasNew) {
        this.incidentTileVersion++
        this.setTiles()
      }

      this.incidentEvents = newList
    },
    playSound(type) {
      if (!this.soundEnabled) {
        console.log('sound not enabled!');
        return;
      }
      if (!this.soundInitialized) {
        console.log('Sound not initialized yet')
        return
      }

      const audio =
          type === 'incident'
              ? this.incidentSound
              : this.anomalySound

      if (!audio) return

      try {
        audio.currentTime = 0
        const p = audio.play()
        if (p && typeof p.then === 'function') {
          p.catch(err => {
            // Avoid "Uncaught (in promise) DOMException"
            console.warn('Audio play was blocked or failed:', err)
          })
        }
      } catch (err) {
        console.warn('Error calling play():', err)
      }
    },
    addRippleMarker(lon, lat, type) {
      if (!this.map) return

      const wrapper = document.createElement('div')
      wrapper.className = 'pulse-wrapper'

      // Animate
      const dot = document.createElement('div')
      dot.className = 'pulse-marker ' + (type === 'incident' ? 'incident' : 'anomaly')
      wrapper.appendChild(dot)

      const marker = new maplibregl.Marker(wrapper, { anchor: 'center' })
          .setLngLat([lon, lat])
          .addTo(this.map)

      const cleanup = () => {
        try { marker.remove() } catch {console.log('not able to remove!')}
        dot.removeEventListener('animationend', cleanup)
      }
      dot.addEventListener('animationend', cleanup)
    }

  },
  created() {
    this.setProfiles()
    this.getExclusionAreas()
  },
  watch: {
    radius(newRadius) {
      console.log("should change tiles to reflect: " + newRadius)
    },
    mmsi(newMmsi) {
      console.log("new: " + newMmsi)
    }
  },
  mounted() {
    this.setupMap()
    this.loadChartValues(this.hours, false)
    Auth.currentUserInfo().then(result => {
      console.log(result)
      this.user = result
    })
    this.initEventBusLive()
    this.initIncidentBusLive()
    EventBus.on("toggle-sound", (val) => {
      this.soundEnabled = val
      if (val && !this.soundInitialized) {
        this.initAudio()
      }
    })
  }
};
</script>
<style scoped>
#mapContainer {
  width: 100%;
  height: 80vh;
}
#lineContainer {
  width: 100%;
  height: 8vh;
}
#app {
  text-align: left;
}
</style>

<style>
/* Wrapper has no animation; MapLibre controls its transform */
.pulse-wrapper {
  position: relative;
  width: 0;
  height: 0;
  pointer-events: none;
  z-index: 2;
}

/* Animated dot */
.pulse-marker {
  position: absolute;
  left: 0;  /* centered by wrapper's own translate from MapLibre */
  top: 0;
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  opacity: 0;
  will-change: transform, opacity;
  animation: marker-life 20s ease-in-out forwards;
}

.pulse-marker.anomaly { background: rgba(225,156,53,0.9); }
.pulse-marker.incident { background: rgba(246,93,76,0.9); }

/* Ripple ring on the dot (not the wrapper) */
.pulse-marker::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 22px;
  height: 22px;
  transform: translate(-50%, -50%) scale(0.2);
  border-radius: 9999px;
  border: 2px solid rgba(255,255,255,0.85);
  opacity: 0;
  will-change: transform, opacity;
  animation: ripple-life 10s ease-out forwards;
}

/* Appear -> grow -> shrink -> vanish */
@keyframes marker-life {
  0%   { transform: scale(0.2); opacity: 0; }
  10%  { transform: scale(0.5); opacity: 1; }
  40%  { transform: scale(1.6); opacity: 1; }
  70%  { transform: scale(0.9); opacity: 0.9; }
  100% { transform: scale(0.0); opacity: 0; }
}

/* Expanding ring */
@keyframes ripple-life {
  0%   { transform: translate(-50%, -50%) scale(0.2); opacity: 0; }
  8%   { opacity: 1; }
  45%  { transform: translate(-50%, -50%) scale(2.2); opacity: 0.9; }
  100% { transform: translate(-50%, -50%) scale(3.0); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .pulse-marker, .pulse-marker::after { animation: none; opacity: 1; }
}

::v-deep(.maplibregl-popup) {
  pointer-events: all;
}
::v-deep(.maplibregl-popup-tip) {
  pointer-events: none;
}

</style>

